import React from 'react';
import styled from 'styled-components';
import { colors } from 'helpers/ColorHelper';
import { media } from 'helpers/MediaHelper';
import { getTranslatedString } from 'helpers/i18n';

import * as Yup from 'yup';
import { Formik, yupToFormErrors } from 'formik';

import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CtaPrimary from 'components/ui/CtaPrimary';

import TicketDetails from './fields/TicketDetails';
import VendorTicketStatus from './fields/VendorTicketStatus';
import VendorNotes from './fields/VendorNotes';
import LandlordLogo from 'components/shared/LandlordLogo';
import Divider from 'components/ui/Divider';
import LanguageOptionsCentered from 'components/shared/LanguageOptionsCentered';

const StyledCard = styled(Card)`
  color: ${colors.DARKEST_GRAY};
  border-radius: 8px;
  box-shadow: none;
  text-align: left;
  padding: 30px;
  width: 600px;
  ${media.mobile`
    width: 100%;
    padding: 15px;
  `};
`;

const FullHeightForm = styled.form`
  height: 100%;
  .ant-form-item {
    flex-direction: column;
  }
`;

const StyledCardContent = styled(CardContent)`
  padding: 0;
`;

const StyledButton = styled(CtaPrimary)`
  background-color: ${(props) =>
    props.disabled ? colors.LIGHT_GREY : colors.GREY_BLUE} !important;
  color: ${(props) => (props.disabled ? colors.CHAR_COUNT : colors.STATE_BLUE)} !important;
  font-size: 18px;
  font-weight: 500;
  width: 270px;
  height: 48px;
  line-height: 48px;
  text-transform: capitalize;
`;

const ButtonCentered = styled.div`
  text-align: center;
  padding-top: 40px;
`;

const StyledDivider = styled(Divider)`
  margin: 20px 40px;
  height: 2px;
  background-color: rgba(0, 0, 0, 0.06);
`;

const StyledLanguageOptionsCenteredContainer = styled.div`
  margin-top: 40px;
`;

const FORM_NAME = 'vendor_maintenance_form';

const MaintenanceVendorForm = ({
  updateTicket,
  formData = {},
  id,
  ticket_number,
  status,
  subject,
  description,
  request_type,
  priority,
  property_name,
  address,
  complete_by,
  property_id,
  buttonDisabled,
}) => {
  const handleSubmit = (values) => {
    updateTicket(values);
  };

  const initialStatus = () => {
    const statuses = ['Fixed', 'vendor_ongoing', 'vendor_other', 'vendor_cancelled'];
    if (statuses.includes(status)) return status;
    return undefined;
  };

  const initialValues = {
    id: id || undefined,
    status: formData.status || initialStatus(),
    note: formData.note,
  };

  const validateForm = async (values) => {
    const requiredErrorVerbiage = getTranslatedString('required');
    let validationSchema = {
      status: Yup.string().required(requiredErrorVerbiage),
      note: Yup.string().required(requiredErrorVerbiage),
    };
    const schema = Yup.object().shape(validationSchema);
    return schema.validate(values).catch((error) => {
      throw yupToFormErrors(error);
    });
  };

  return (
    <Formik initialValues={initialValues} validate={validateForm} onSubmit={handleSubmit}>
      {({ handleSubmit, ...rest }) => (
        <FullHeightForm id={FORM_NAME} onSubmit={handleSubmit}>
          <StyledCard>
            <StyledCardContent expandable={true}>
              <LandlordLogo maxWidth="137px" height="46px" textHeight="30px" />
              <StyledDivider orientation="center" />

              <TicketDetails
                ticket_number={ticket_number}
                property_name={property_name}
                address={address}
                subject={subject}
                request_type={request_type}
                priority={priority}
                description={description}
                complete_by={complete_by}
                {...rest}
              />
              <VendorTicketStatus status={status} {...rest} />
              <VendorNotes {...rest} />
            </StyledCardContent>
            <ButtonCentered>
              <StyledButton type="submit" disabled={buttonDisabled}>
                {getTranslatedString('save')}
              </StyledButton>
            </ButtonCentered>
            <StyledLanguageOptionsCenteredContainer>
              <LanguageOptionsCentered onLanguageChange={() => window.location.reload()} />
            </StyledLanguageOptionsCenteredContainer>
          </StyledCard>
        </FullHeightForm>
      )}
    </Formik>
  );
};

export default MaintenanceVendorForm;
